// BotManager (§10, §22): the frozen surface (`reset`, `setBot`, `hasBot`, `observe`, `inputsFor`)
// over the full bot AI. One manager per room (or per LocalSession / autoplay client) owns the
// shared pieces: the safety field (§10.1), the hazard forecast, the perception ring and the target
// counts behind the hard max-2-bots-per-target rule. Each tick, per bot:
//   perception (others as seen `reactionTicks` ago) → state handler or, on its feet, the brain
//   (utility think every 6 ticks, staggered) → goal tactic, reflexes, item heuristics → RECOVER
//   override → social layer → motor (smoothed stick, presses, aim noise) → InputFrame.
// Bots output exactly the InputFrame a human produces; the sim cannot tell them apart (§10).
import { MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { EV } from '../events';
import { BS, MapId, RoundPhase } from '../ids';
import { MAPS } from '../maps/index';
import { roundClock } from '../maps/clock';
import { chance } from '../rng';
import { hasBody } from '../sim/bean';
import { NEUTRAL_INPUT, type Bean, type InputFrame, type Vec2, type World } from '../types';
import type { BotConfig, BotContext } from './botTypes';
import { Goal, createBrain, goalTargetsBean, resetBrainForRound, type Brain } from './brain';
import { createCtx, createTelemetry, type BotTelemetry, type Ctx } from './context';
import { releaseTarget } from './goals';
import { HazardForecast } from './hazards';
import { cakeHop } from './mapTactics';
import { bombGrab, itemReflexes, itemThink } from './itemTactics';
import {
  crownFetch, crownHold, cupRide, dance, ghost, ghostThink, lobby, podium, potatoFlee, potatoPass, respawnTarget,
} from './modeTactics';
import { clearWant, runMotor } from './motor';
import { Perception } from './perception';
import { SafetyField } from './safetyField';
import { carryGrudge, socialEvent, socialTick } from './social';
import {
  carrying, climbPayoff, climbRetreat, enterState, falling, float, grabbed, ledge, respawn, spun, teeter, tumble, windup,
} from './states';
import { anticipate, attack, chaosShove, edgeRecover, evade, fetchCrate, reflexes, wander } from './tactics';
import { think } from './think';
import { BOT_TUNE } from './tuning';

const RESPAWN_AT: Vec2 = { x: 0, y: 0 };

export class BotManager {
  private readonly brains: (Brain | null)[] = new Array<Brain | null>(MAX_BEANS).fill(null);
  /** §10.1 safety field (shared by every brain of the room). */
  readonly field = new SafetyField();
  readonly hazards = new HazardForecast();
  readonly perception = new Perception();
  /** §10.2 `BotManager.targetCounts`: brains currently targeting each bean (never above TUNE.bot.maxPerTarget). */
  readonly targetCounts: number[] = new Array<number>(MAX_BEANS).fill(0);
  /** Cumulative counters for the harness and debug tools (§30.3). */
  readonly telemetry: BotTelemetry = createTelemetry();
  private readonly ctx: Ctx;
  private readonly human = new Array<boolean>(MAX_BEANS).fill(false);
  /** Rounds seen by `reset` (grudges carry into the next one). */
  private round = 0;
  private rebuildPending = true;

  constructor() {
    this.ctx = createCtx(this.field, this.hazards, this.perception, this.targetCounts, this.telemetry);
    this.ctx.human = this.human;
  }

  /** New world: rebuild the safety field, clear perception and targets, start each brain's round. */
  reset(w: World): void {
    this.round++;
    this.field.build(w);
    this.rebuildPending = false;
    this.hazards.reset();
    this.perception.clear();
    this.targetCounts.fill(0);
    this.ctx.duelSince = -1;
    this.ctx.lastKoTick = 0;
    for (const br of this.brains) {
      if (!br) continue;
      resetBrainForRound(br);
      carryGrudge(br, this.round);
      br.synced = false;
    }
  }

  /** Add / replace / remove the brain for a slot. */
  setBot(slot: number, cfg: BotConfig | null): void {
    const old = this.brains[slot];
    if (old) releaseTarget(this.targetCounts, old);
    this.brains[slot] = cfg ? createBrain(cfg, TUNE.bot.thinkEveryTicks) : null;
  }

  hasBot(slot: number): boolean {
    return this.brains[slot] !== null;
  }

  /** Debug and harness access to a brain (read-only by convention). */
  brainOf(slot: number): Readonly<Brain> | null {
    return this.brains[slot] ?? null;
  }

  /**
   * After each step: record the world into the perception ring and read the step's events
   * (grudges, honk-backs, emotes, dance rolls, retaliation memory, ground changes).
   */
  observe(w: World): void {
    this.perception.record(w);
    for (const ev of w.events) {
      if (ev.type === EV.TILE || ev.type === EV.WEDGE) this.rebuildPending = true;
      if (ev.type === EV.KO) this.ctx.lastKoTick = w.tick;
      for (let slot = 0; slot < MAX_BEANS; slot++) {
        const br = this.brains[slot];
        if (br) socialEvent(w, br, slot, ev, this.round, this.human);
      }
    }
  }

  /**
   * Writes a frame for every bot-driven slot into `out[slot]` (reusing the object when present;
   * a new frame gets `tick = w.tick + 1`, which callers overwrite with their own room tick) and
   * leaves other slots untouched.
   */
  inputsFor(w: World, bc: BotContext, out: (InputFrame | null)[]): void {
    let any = false;
    for (let slot = 0; slot < MAX_BEANS; slot++) if (this.brains[slot]) any = true;
    if (!any) return;
    const ctx = this.ctx;
    ctx.w = w;
    ctx.bc = bc;
    this.refreshShared(w, bc);
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const br = this.brains[slot];
      if (!br) continue;
      let f = out[slot];
      if (!f) {
        f = { ...NEUTRAL_INPUT, tick: w.tick + 1 };
        out[slot] = f;
      }
      this.drive(br, w.beans[slot], f);
    }
    // The hard rule (§10.2): verify it after every tick.
    for (let s = 0; s < MAX_BEANS; s++) {
      if (this.targetCounts[s] > TUNE.bot.maxPerTarget) {
        this.telemetry.capViolations++;
        break;
      }
    }
  }

  // ---- shared per-tick state ------------------------------------------------------------------

  private refreshShared(w: World, bc: BotContext): void {
    const ctx = this.ctx;
    const field = this.field;
    const dynamic = w.cfg.mapId === MapId.CAKE
      || (w.cfg.mapId === MapId.TEACUP && (w.phase === RoundPhase.SUDDEN_DEATH || w.map.wedgeWarn !== 0 || w.map.wedgeFallen !== 0));
    const every = w.cfg.mapId === MapId.CAKE ? BOT_TUNE.cake.rebuildTicks : TUNE.bot.safetyRebuildTicks;
    const stale = w.tick < field.builtTick || w.tick - field.builtTick >= every;
    if (field.mapId !== w.cfg.mapId || this.rebuildPending || (dynamic && stale)) {
      field.build(w);
      this.rebuildPending = false;
    }
    this.perception.ensure(w);
    this.hazards.update(w);
    roundClock(w.cfg.mapId, w.cfg.modeId, w.tick, w.cfg.capTickOverride, ctx.clock);
    this.human.fill(false);
    for (const s of bc.humanSlots) if (s >= 0 && s < MAX_BEANS) this.human[s] = true;
    // Match leader: strictly highest score with at least one point (§4.1 crowned rule).
    let best = 0;
    let leader = NO_SLOT;
    for (let s = 0; s < MAX_BEANS; s++) {
      const v = bc.matchScores[s] ?? 0;
      if (v > best) {
        best = v;
        leader = s;
      } else if (v === best && v > 0) leader = NO_SLOT;
    }
    ctx.leader = leader;
    // Action centroid (§10.2 WANDER).
    let n = 0;
    let cx = 0;
    let cy = 0;
    for (const b of w.beans) {
      if (!hasBody(b)) continue;
      cx += b.pos.x;
      cy += b.pos.y;
      n++;
    }
    const c = MAPS[w.cfg.mapId].center;
    ctx.alive = n;
    if (n > 2 || w.tick < 0) ctx.duelSince = -1;
    else if (ctx.duelSince < 0 || ctx.duelSince > w.tick) ctx.duelSince = w.tick;
    ctx.cx = n > 0 ? cx / n : c.x;
    ctx.cy = n > 0 ? cy / n : c.y;
    // Anchor claims (Cake): a brain holding or heading for a tile claims it while it wanders.
    ctx.anchorClaims.fill(0);
    for (let s = 0; s < MAX_BEANS; s++) {
      const br = this.brains[s];
      if (br && br.anchorTile >= 0 && br.goal === Goal.WANDER && hasBody(w.beans[s])) ctx.anchorClaims[br.anchorTile]++;
    }
        // Recount the targets from the brains themselves, so the counts can never drift.
    this.targetCounts.fill(0);
    for (const br of this.brains) {
      if (br && goalTargetsBean(br.goal) && br.target !== NO_SLOT) this.targetCounts[br.target]++;
    }
  }

  // ---- one bot, one tick ------------------------------------------------------------------------

  private drive(br: Brain, b: Bean, f: InputFrame): void {
    const ctx = this.ctx;
    const w = ctx.w;
    if (!br.synced) {
      // Continue from the counters the sim last decoded for this bean: no phantom press on takeover.
      br.cShove = b.prev.shove & 15;
      br.cGrab = b.prev.grab & 15;
      br.cJump = b.prev.jump & 15;
      br.cItem = b.prev.item & 15;
      br.cHonk = b.prev.honk & 7;
      br.cEmote = b.prev.emote & 3;
      br.synced = true;
    }
    f.mx = f.my = f.ax = f.ay = f.sx = f.sy = 0;
    f.held = 0;
    f.emote = 0;
    if (b.active) {
      ctx.self = b;
      ctx.brain = br;
      ctx.frame = f;
      ctx.delay = Math.round(br.p.reactionTicks * Math.max(1, ctx.bc.reactionMult));
      // Just climbed back (no second ledge for 5 s): a person keeps well away from the rim.
      const shaken = b.cdLedge > 0 ? BOT_TUNE.usedLedgeMarginMult : 1;
      const mapMargin = br.p.edgeMargin * (BOT_TUNE.mapMargin[w.cfg.mapId] ?? 1) * shaken;
      ctx.margin = Math.min(mapMargin, Math.max(BOT_TUNE.marginFloor, this.field.maxSafety * BOT_TUNE.marginCapFrac));
      for (let s = 0; s < MAX_BEANS; s++) {
        const o = ctx.seen[s];
        if (s === b.slot) {
          o.x = b.pos.x;
          o.y = b.pos.y;
          o.vx = b.vel.x;
          o.vy = b.vel.y;
          o.z = b.z;
          o.state = b.state;
          o.facing = b.facing;
          o.st = b.st;
          o.body = hasBody(b);
          o.tick = w.tick;
        } else this.perception.view(w, s, w.tick, ctx.delay, o);
      }
      if (b.item !== br.itemKind) {
        br.itemKind = b.item;
        br.itemSince = w.tick;
      }
      if (b.state !== br.lastState) {
        br.prevState = br.lastState;
        br.lastState = b.state;
        br.stateTick = w.tick;
        enterState(ctx);
      }
      clearWant(ctx);
      br.waiting = false;
      br.hazardOwned = false;
      this.act(ctx, br, b);
      const social = socialTick(w, br, b.state);
      if (social & 1) {
        br.cHonk = (br.cHonk + 1) & 7;
        ctx.tele.honks++;
      }
      if (social & 2) {
        br.cEmote = (br.cEmote + 1) & 3;
        f.emote = br.emote;
        ctx.tele.emotes++;
      }
      runMotor(ctx);
      ctx.tele.goalTicks[br.goal]++;
    }
    f.cShove = br.cShove;
    f.cGrab = br.cGrab;
    f.cJump = br.cJump;
    f.cItem = br.cItem;
    f.cHonk = br.cHonk;
    f.cEmote = br.cEmote;
  }

  /** Drops a pursuit target (the bean lost its body, or plays a non-pursuit state). */
  private dropPursuit(br: Brain): void {
    if (goalTargetsBean(br.goal) && br.target !== NO_SLOT) {
      releaseTarget(this.targetCounts, br);
      br.goal = Goal.WANDER;
      br.target = NO_SLOT;
    }
  }

  private act(ctx: Ctx, br: Brain, b: Bean): void {
    const w = ctx.w;
    switch (b.state) {
      case BS.FROZEN:
        br.want.precise = true;
        return;
      case BS.GHOST:
        this.dropPursuit(br);
        if (br.forceThink || (w.tick + br.phase) % TUNE.bot.thinkEveryTicks === 0) {
          br.forceThink = false;
          ghostThink(ctx);
        }
        ghost(ctx);
        return;
      case BS.RESPAWN:
        this.dropPursuit(br);
        respawnTarget(ctx, RESPAWN_AT);
        respawn(ctx, RESPAWN_AT.x, RESPAWN_AT.y);
        return;
      case BS.FALLING:
        this.dropPursuit(br);
        falling(ctx);
        return;
      case BS.TEETER:
        teeter(ctx);
        return;
      case BS.LEDGE:
        ledge(ctx);
        return;
      case BS.GRABBED:
        grabbed(ctx);
        return;
      case BS.TUMBLE:
        tumble(ctx);
        return;
      case BS.FLOAT:
        float(ctx);
        return;
      case BS.CARRYING:
        carrying(ctx);
        return;
      case BS.SPUN:
        spun(ctx);
        return;
      case BS.PUNCH_WINDUP:
        windup(ctx);
        return;
      case BS.GRAB_MISS:
        // The punish window: shuffle back toward safety at 30% speed.
        edgeRecover(ctx, ctx.margin);
        br.want.precise = true;
        return;
      case BS.NORMAL:
        this.play(ctx, br, b);
        return;
      default:
        // LUNGE, PUNCH, GRAB_START, CLIMB, SLIP, YANKED: committed; keep the stick quiet.
        br.want.precise = true;
        return;
    }
  }

  /** A bean on its feet (§10.2): think, reflexes, goal tactic, items, then the RECOVER override. */
  private play(ctx: Ctx, br: Brain, b: Bean): void {
    const w = ctx.w;
    if (br.target !== NO_SLOT && goalTargetsBean(br.goal) && !ctx.seen[br.target].body) br.forceThink = true;
    if (w.tick >= br.brawlUntil) {
      br.brawlMode = chance(br, TUNE.bot.fun.brawlChance);
      br.brawlUntil = w.tick + BOT_TUNE.think.brawlRollTicks;
    }
    if (br.forceThink || (w.tick + br.phase) % TUNE.bot.thinkEveryTicks === 0) {
      think(ctx);
      itemThink(ctx);
      anticipate(ctx);
    }
    if (!climbPayoff(ctx) && !reflexes(ctx) && !climbRetreat(ctx) && !chaosShove(ctx)) {
      switch (br.goal) {
        case Goal.ATTACK: attack(ctx); break;
        case Goal.EVADE: evade(ctx); break;
        case Goal.ITEM: fetchCrate(ctx); break;
        case Goal.POTATO_PASS: potatoPass(ctx); break;
        case Goal.POTATO_FLEE: potatoFlee(ctx); break;
        case Goal.CROWN_FETCH: crownFetch(ctx); break;
        case Goal.CROWN_HOLD: crownHold(ctx); break;
        case Goal.LOBBY: lobby(ctx); break;
        case Goal.DANCE: dance(ctx); break;
        case Goal.PODIUM: podium(ctx); break;
        case Goal.CUP: cupRide(ctx); break;
        case Goal.BOMB_GRAB: bombGrab(ctx); break;
        default: wander(ctx); break;
      }
    }
    itemReflexes(ctx);
    if (br.want.mode === 1 && !br.press.shove && !br.press.grab) cakeHop(ctx, br.want.px, br.want.py);
    // RECOVER (§10.2): checked every tick and always wins.
    // Hopping an arm means running at it: only a real emergency overrides that.
    const margin = br.hazardOwned ? Math.min(br.want.margin, ctx.margin, BOT_TUNE.hazardRecoverMargin) : Math.min(br.want.margin, ctx.margin);
    if (br.goal !== Goal.CUP && edgeRecover(ctx, margin)) {
      ctx.tele.recoverTicks++;
      // Never lunge or grab outward while saving yourself.
      const pr = br.press;
      if (pr.shove || pr.grab) {
        ctx.field.gradient(b.pos.x, b.pos.y, RESPAWN_AT);
        if (pr.sx * RESPAWN_AT.x + pr.sy * RESPAWN_AT.y < 0) pr.shove = pr.grab = false;
      }
    }
  }
}
